/**
 * QR Code Page - Trade Loop v2.0
 *
 * Allows users to:
 * 1. Display their personal QR code for others to scan
 * 2. Scan another user's QR code to connect as partners
 * 3. Share their QR link to invite new users
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { isNativeScannerAvailable, scanQrCode, ScanError } from "@/services/qrScanner";
import { shareableUrl } from "@/utils/shareableUrl";
import { useShare } from "@/hooks/useShare";
import {
  QrCode,
  Camera,
  Copy,
  Share2,
  Download,
  CheckCircle,
  Users,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";

// QR Code Generator Component using Canvas API
const QRCodeGenerator = ({
  value,
  size = 256,
}: {
  value: string;
  size?: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const generateQR = async () => {
      if (!canvasRef.current) return;

      try {
        setIsLoading(true);
        setError(null);

        // Dynamic import of qrcode library
        const QRCode = await import("qrcode");

        await QRCode.toCanvas(canvasRef.current, value, {
          width: size,
          margin: 2,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
          errorCorrectionLevel: "H",
        });

        setIsLoading(false);
      } catch (err) {
        console.error("QR Generation error:", err);
        setError("Failed to generate QR code");
        setIsLoading(false);
      }
    };

    generateQR();
  }, [value, size]);

  if (error) {
    return (
      <div className="flex items-center justify-center bg-muted rounded-lg" style={{ width: size, height: size }}>
        <p className="text-sm text-muted-foreground text-center px-4">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg"
          style={{ width: size, height: size }}
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={`rounded-lg ${isLoading ? "opacity-0" : "opacity-100"}`}
      />
    </div>
  );
};

// QR Scanner Component
const QRScanner = ({
  onScan,
  onClose,
}: {
  onScan: (data: string) => void;
  onClose: () => void;
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const scannerRef = useRef<any>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let html5QrCode: any = null;

    const startScanner = async () => {
      try {
        setDebugInfo("Requesting camera permission...");

        // First, explicitly request camera permission
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" }
          });
          // Stop the stream immediately - we just needed permission
          stream.getTracks().forEach(track => track.stop());
          setDebugInfo("Permission granted, starting scanner...");
        } catch (permErr: any) {
          console.error("Permission error:", permErr);
          if (permErr.name === "NotAllowedError") {
            setError("Camera access denied. Please allow camera access in your browser settings.");
            return;
          } else if (permErr.name === "NotFoundError") {
            setError("No camera found on this device.");
            return;
          }
          // Continue anyway - some browsers handle permissions differently
          setDebugInfo("Permission check skipped, trying scanner...");
        }

        // Dynamic import of html5-qrcode
        const { Html5Qrcode } = await import("html5-qrcode");

        if (!mountedRef.current) return;

        html5QrCode = new Html5Qrcode("qr-reader", { verbose: false });
        scannerRef.current = html5QrCode;

        // Calculate responsive qrbox size
        const screenWidth = window.innerWidth;
        const qrboxSize = Math.min(250, screenWidth - 80);

        // Try to get available cameras first
        let cameraConfig: any = { facingMode: "environment" };

        try {
          const cameras = await Html5Qrcode.getCameras();
          setDebugInfo(`Found ${cameras.length} camera(s)`);

          if (cameras && cameras.length > 0) {
            // Prefer back camera on mobile
            const backCamera = cameras.find(
              (c: any) => c.label.toLowerCase().includes("back") ||
                         c.label.toLowerCase().includes("rear") ||
                         c.label.toLowerCase().includes("environment")
            );
            if (backCamera) {
              cameraConfig = backCamera.id;
            } else {
              // Use the last camera (usually back camera on mobile)
              cameraConfig = cameras[cameras.length - 1].id;
            }
          }
        } catch (camErr) {
          console.log("Could not enumerate cameras, using default:", camErr);
          setDebugInfo("Using default camera config...");
        }

        await html5QrCode.start(
          cameraConfig,
          {
            fps: 10,
            qrbox: { width: qrboxSize, height: qrboxSize },
            aspectRatio: 1,
          },
          (decodedText: string) => {
            // Successfully scanned
            if (scannerRef.current) {
              scannerRef.current.stop().catch(console.error);
            }
            onScan(decodedText);
          },
          () => {
            // QR code not found in frame - this is normal, don't log
          }
        );

        if (mountedRef.current) {
          setIsScanning(true);
          setDebugInfo("");
        }
      } catch (err: any) {
        console.error("Scanner error:", err);
        if (!mountedRef.current) return;

        const errorMsg = err?.message || err?.toString() || "Unknown error";

        if (errorMsg.includes("Permission denied") || err.name === "NotAllowedError") {
          setError("Camera access denied. Please allow camera access in your browser settings and reload the page.");
        } else if (errorMsg.includes("NotFoundError") || err.name === "NotFoundError" || errorMsg.includes("Requested device not found")) {
          setError("No camera found on this device.");
        } else if (errorMsg.includes("NotReadableError") || err.name === "NotReadableError") {
          setError("Camera is in use by another application. Please close other apps using the camera.");
        } else if (errorMsg.includes("OverconstrainedError")) {
          setError("Camera doesn't support the required settings. Try using a different browser.");
        } else if (errorMsg.includes("NotSupportedError") || errorMsg.includes("HTTPS") || errorMsg.includes("not supported")) {
          setError("Camera streaming requires HTTPS. Please access this page over a secure connection.");
        } else {
          setError(`Camera error: ${errorMsg}`);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(startScanner, 100);

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      if (scannerRef.current) {
        try {
          scannerRef.current.stop().catch(() => {});
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [onScan]);

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">Scan QR Code</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Camera className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-sm text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload Page
          </Button>
        </div>
      ) : (
        <div className="relative">
          <div
            id="qr-reader"
            className="w-full overflow-hidden rounded-lg"
            style={{ minHeight: 300 }}
          />
          {!isScanning && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted rounded-lg">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              {debugInfo && (
                <p className="text-xs text-muted-foreground">{debugInfo}</p>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-sm text-muted-foreground text-center mt-4">
        Point your camera at a Trade Loop QR code
      </p>
    </div>
  );
};

const QRPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("my-qr");
  const [showScanner, setShowScanner] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const { share, canShare } = useShare();

  // Redirect team members (technician/member) - they cannot access QR page
  useEffect(() => {
    if (user?.organizationMembership) {
      const role = user.organizationMembership.role;
      if (role === 'member') {
        showError('Access Denied', 'Team members cannot access the QR code page');
        navigate('/organization', { replace: true });
      }
    }
  }, [user, navigate, showError]);

  // Generate the connect URL for this user
  // Public origin, not window.location.origin: on a bundled native build the WebView
  // origin is capacitor://localhost, and this URL goes into a QR code, the clipboard and
  // the share sheet — i.e. straight to another person's phone.
  const connectUrl = shareableUrl(`/connect/${user?.id || ""}`);

  // Connect with another user
  const connectMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      // Use instant-connect endpoint for QR code connections
      // This creates bidirectional "accepted" connections immediately
      const payload = {
        partnerId: partnerId
      };

      return api.post("/api/workflows/connections/instant-connect", payload);
    },
    onSuccess: () => {
      showSuccess("Connected!", "You are now connected as partners");
      navigate("/network");
    },
    onError: (error: any) => {
      // Extract detailed error message from backend
      const errorMessage = error.data?.error || error.message || "";

      if (errorMessage.includes("already") || errorMessage.includes("Already connected")) {
        showSuccess("Already Connected", "You are already connected with this partner");
        navigate("/network");
      } else {
        const errorDetails = error.data?.details || "";
        showError("Connection Failed", errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage || "Could not connect with this user");

        // Log full error for debugging
        console.error("[QRPage] Connection request failed:", {
          partnerId,
          error,
          errorData: error.data,
          status: error.status
        });
      }
    },
  });

  // Handle scanned QR code
  const handleScan = (data: string) => {
    setShowScanner(false);

    // Parse the scanned URL
    try {
      const url = new URL(data);
      const pathParts = url.pathname.split("/");
      const connectIndex = pathParts.indexOf("connect");

      if (connectIndex !== -1 && pathParts[connectIndex + 1]) {
        const partnerId = pathParts[connectIndex + 1];

        // Check if scanning own QR code
        if (partnerId === user?.id) {
          showError("Oops!", "You can't connect with yourself");
          return;
        }

        // Initiate connection
        connectMutation.mutate(partnerId);
      } else {
        showError("Invalid QR Code", "This doesn't appear to be a Trade Loop QR code");
      }
    } catch {
      showError("Invalid QR Code", "Could not read the QR code");
    }
  };

  // Open the scanner. On native this is the full-screen scanner owned by the app
  // (@capacitor/barcode-scanner) — no in-page <video>, no getUserMedia. On web it mounts
  // the html5-qrcode component below, which is still the right answer in a browser.
  const handleOpenScanner = async () => {
    if (!isNativeScannerAvailable()) {
      setShowScanner(true);
      return;
    }

    try {
      const scanned = await scanQrCode();
      // null == the user closed the scanner. Nothing to say about that.
      if (scanned) handleScan(scanned);
    } catch (error) {
      if (error instanceof ScanError) {
        showError(error.reason === 'denied' ? 'Camera Permission Needed' : 'Scanner Failed', error.message);
        return;
      }
      console.error('[QRPage] Native scan failed:', error);
      showError('Scanner Failed', 'The scanner could not start. Please try again.');
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(connectUrl);
      setCopiedLink(true);
      showSuccess("Link Copied", "Share this link to connect with partners");
      setTimeout(() => setCopiedLink(false), 3000);
    } catch {
      showError("Copy Failed", "Could not copy link to clipboard");
    }
  };

  // Share invite link. On native (iOS/Android) and web browsers with the Web Share API,
  // delegates to the OS share sheet via useShare (which uses @capacitor/share on native).
  // Only falls back to the mailto: email flow on desktop web that has no share capability —
  // never on native, so the Android WebView (which lacks navigator.share) still gets the
  // system share sheet instead of the email client.
  const handleShare = async () => {
    const userName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || 'A Trade Loop member';

    const shareTitle = `Join me on Trade Loop`;
    const shareText = `${userName} invited you to join Trade Loop - a professional network for trade professionals to connect and share referrals.`;

    if (!canShare) {
      openEmailFallback();
      return;
    }

    const shared = await share({
      title: shareTitle,
      text: shareText,
      url: connectUrl,
    });

    if (shared) {
      showSuccess("Shared!", "Your invite link has been shared");
    }
    // If !shared, it was either a user cancel (silent) or an error already logged by the
    // hook. Do not fall back to email on native — that is the exact bug this fix removes.
  };

  // Email fallback for desktop browsers
  const openEmailFallback = () => {
    const userName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || 'A Trade Loop member';

    const subject = `${userName} invited you to join Trade Loop`;

    // Build professional signature
    const signatureParts = [];

    if (user?.firstName && user?.lastName) {
      signatureParts.push(`${user.firstName} ${user.lastName}`);
    } else if (user?.firstName) {
      signatureParts.push(user.firstName);
    } else if (user?.email) {
      signatureParts.push(user.email);
    }

    if (user?.trade) {
      signatureParts.push(user.trade);
    }

    if (user?.company) {
      signatureParts.push(user.company);
    }

    if (user?.email) {
      signatureParts.push(`Email: ${user.email}`);
    }

    if (user?.phone) {
      signatureParts.push(`Phone: ${user.phone}`);
    }

    const signature = signatureParts.join('\n');

    const body = `Hi there!

I'd like to invite you to join me on Trade Loop - a professional network where trade professionals connect and share referrals.

Trade Loop makes it easy to:
• Build your professional network
• Exchange qualified referrals
• Grow your business with trusted partners

Join me by clicking the link below:
${connectUrl}

Looking forward to connecting with you!

Best regards,

${signature}`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    showSuccess("Email Opened", "Your email client has been opened with the invitation");
  };

  // Download QR code as image
  const handleDownload = () => {
    const canvas = document.querySelector("canvas");
    if (canvas) {
      const link = document.createElement("a");
      link.download = "trade-loop-qr.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      showSuccess("Downloaded", "QR code saved to your device");
    }
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-6 max-w-lg">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6 p-4 bg-gradient-to-r from-blue-500 via-blue-600 to-green-500 rounded-xl shadow-lg">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <QrCode className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">QR Connect</h1>
            <p className="text-sm text-white/80">
              Instantly connect with partners
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-gradient-to-r from-blue-100 to-green-100 p-1">
            <TabsTrigger value="my-qr" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white">
              <QrCode className="h-4 w-4 mr-2" />
              My QR Code
            </TabsTrigger>
            <TabsTrigger value="scan" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-green-500 data-[state=active]:text-white">
              <Camera className="h-4 w-4 mr-2" />
              Scan
            </TabsTrigger>
          </TabsList>

          {/* My QR Code Tab */}
          <TabsContent value="my-qr">
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardHeader className="text-center pb-2 bg-gradient-to-br from-blue-50 via-blue-50 to-green-50">
                <CardTitle className="text-lg bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Your Partner Code</CardTitle>
                <p className="text-sm text-blue-600/70">
                  Others can scan this to connect with you
                </p>
              </CardHeader>
              <CardContent className="flex flex-col items-center bg-gradient-to-b from-white to-blue-50/30 pt-6">
                {/* QR Code */}
                <div className="bg-white p-4 rounded-2xl shadow-lg ring-4 ring-blue-100 mb-6">
                  <QRCodeGenerator value={connectUrl} size={220} />
                </div>

                {/* User Info */}
                <div className="text-center mb-6 px-4 py-2 bg-gradient-to-r from-blue-100 to-green-100 rounded-full">
                  <p className="font-semibold text-blue-700">{user?.fullName || 'User'}</p>
                  {user?.trade && (
                    <p className="text-sm text-blue-600">{user.trade}</p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-3 gap-3 w-full">
                  <Button
                    variant="outline"
                    className="flex flex-col items-center py-4 h-auto border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all"
                    onClick={handleCopyLink}
                  >
                    {copiedLink ? (
                      <CheckCircle className="h-5 w-5 mb-1 text-green-500" />
                    ) : (
                      <Copy className="h-5 w-5 mb-1 text-blue-600" />
                    )}
                    <span className="text-xs text-blue-700">Copy Link</span>
                  </Button>
                  <Button
                    variant="outline"
                    data-tour-target="qr-share"
                    className="flex flex-col items-center py-4 h-auto border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all"
                    onClick={handleShare}
                  >
                    <Share2 className="h-5 w-5 mb-1 text-blue-600" />
                    <span className="text-xs text-blue-700">Share</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="flex flex-col items-center py-4 h-auto border-green-200 hover:bg-green-50 hover:border-green-300 transition-all"
                    onClick={handleDownload}
                  >
                    <Download className="h-5 w-5 mb-1 text-green-600" />
                    <span className="text-xs text-green-700">Download</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* How it works */}
            <Card className="mt-4 border-0 shadow-lg bg-gradient-to-br from-blue-50 via-blue-50 to-green-50">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-blue-700">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  How It Works
                </h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
                    <span className="text-gray-700">Show your QR code or share your link</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-600 to-green-500 text-white text-xs font-bold flex items-center justify-center">2</span>
                    <span className="text-gray-700">Partner scans or clicks the link</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                    <span className="text-gray-700">Instantly connected - start sending referrals!</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scan Tab */}
          <TabsContent value="scan">
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardContent className="pt-6 bg-gradient-to-b from-white to-blue-50/50">
                {showScanner ? (
                  <QRScanner
                    onScan={handleScan}
                    onClose={() => setShowScanner(false)}
                  />
                ) : (
                  <div className="flex flex-col items-center py-8">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-green-500 flex items-center justify-center mb-5 shadow-lg shadow-blue-200">
                      <Camera className="h-12 w-12 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2 bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Scan a Partner's QR Code</h3>
                    <p className="text-sm text-blue-600/70 text-center mb-6 max-w-xs">
                      Scan another Trade Loop user's QR code to instantly connect as partners
                    </p>
                    <Button
                      onClick={() => void handleOpenScanner()}
                      size="lg"
                      className="bg-gradient-to-r from-blue-500 via-blue-600 to-green-500 hover:from-blue-600 hover:via-blue-700 hover:to-green-600 shadow-lg shadow-blue-200 transition-all"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Open Camera
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tips */}
            {!showScanner && (
              <Card className="mt-4 border-0 shadow-lg bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-3 text-amber-700 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs">💡</span>
                    Tips
                  </h3>
                  <ul className="space-y-2 text-sm text-amber-800/80">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                      Make sure the QR code is well-lit
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400"></span>
                      Hold your camera steady
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                      Position the QR code within the frame
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Loading overlay for connection */}
        {connectMutation.isPending && (
          <div className="fixed inset-0 bg-blue-900/20 backdrop-blur-sm flex items-center justify-center z-50">
            <Card className="w-64 border-0 shadow-2xl overflow-hidden">
              <CardContent className="flex flex-col items-center py-8 bg-gradient-to-br from-blue-50 via-blue-50 to-green-50">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-green-500 flex items-center justify-center mb-4 shadow-lg">
                  <Loader2 className="h-8 w-8 animate-spin text-white" />
                </div>
                <p className="font-semibold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Connecting...</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default QRPage;
